import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import DraggableFlatList from 'react-native-draggable-flatlist';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { getProjectModuleCapabilities } from '../../config/projectCapabilities';
import { useAuth } from '../../context/AuthContext';
import { createProject } from '../../services/projectService';
import type {
  ProjectColumnDraft,
  ProjectMemberDraft,
  ProjectMemberRole,
} from '../../types/project';
import { PROJECT_MEMBER_ROLES } from '../../types/project';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  createDefaultProjectColumns,
  createEmptyProjectColumn,
  isFixedProjectColumn,
  nextProjectDraftKey,
  pinFixedProjectColumns,
  projectMemberRoleLabel,
  toProjectCreatePayload,
  validateProjectForm,
} from '../../utils/projectForm';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';
import {
  employeeSelectorOption,
  loadOrganizationEmployeeOptions,
  loadTeamEmployeeOptions,
} from '../../utils/projectEmployeeOptions';

type Props = NativeStackScreenProps<RootStackParamList, 'ProjectCreate'>;

function TrashIcon() {
  return (
    <View style={trashStyles.root} accessibilityElementsHidden>
      <View style={trashStyles.lid} />
      <View style={trashStyles.body}>
        <View style={trashStyles.line} />
        <View style={trashStyles.line} />
        <View style={trashStyles.line} />
      </View>
    </View>
  );
}

const trashStyles = StyleSheet.create({
  root: { width: 18, height: 20, alignItems: 'center' },
  lid: {
    width: 14,
    height: 3,
    backgroundColor: '#EF4444',
    borderRadius: 2,
    marginBottom: 2,
  },
  body: {
    width: 12,
    height: 14,
    borderWidth: 1.8,
    borderColor: '#EF4444',
    borderRadius: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    overflow: 'hidden',
    paddingHorizontal: 1,
  },
  line: {
    width: 1.5,
    height: 8,
    backgroundColor: '#EF4444',
    borderRadius: 1,
  },
});

export { employeeSelectorOption };

export function ProjectFormScreen({ navigation }: Props) {
  const auth = useAuth();
  const capabilities = getProjectModuleCapabilities(auth.permissions, auth.roles);
  const teamOnly = capabilities.scope === 'CREATOR';
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.xs,
      },
      hint: { ...typography.caption, color: palette.textSecondary },
      error: { ...typography.body, color: palette.error },
      label: { ...typography.caption, color: palette.textMuted },
      memberName: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '600' as const,
        flex: 1,
      },
      memberRole: { ...typography.caption, color: palette.textSecondary },
      chip: {
        borderWidth: 1,
        borderColor: palette.border,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipActive: {
        borderWidth: 1,
        borderColor: palette.brand,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.brand,
      },
      chipText: { ...typography.caption, color: palette.textPrimary },
      chipTextActive: { ...typography.caption, color: palette.white, fontWeight: '700' as const },
      move: { ...typography.caption, color: palette.primary, fontWeight: '700' as const },
      remove: { ...typography.caption, color: palette.error, fontWeight: '700' as const },
      inputText: { ...typography.body, color: palette.textPrimary },
    }),
    [palette],
  );

  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState<ProjectMemberDraft[]>([]);
  const [pendingEmployee, setPendingEmployee] = useState<SelectorOption | null>(
    null,
  );
  const [pendingRole, setPendingRole] = useState<ProjectMemberRole | null>(null);
  const [columns, setColumns] = useState<ProjectColumnDraft[]>(
    createDefaultProjectColumns,
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function addMember(employee: SelectorOption, role: ProjectMemberRole) {
    if (members.some(member => member.employeeId === employee.id)) {
      setError('The same employee cannot be assigned twice.');
      return false;
    }
    setMembers(current => [
      ...current,
      {
        key: nextProjectDraftKey('member'),
        employeeId: employee.id,
        employeeName: employee.label,
        role,
      },
    ]);
    setPendingEmployee(null);
    setPendingRole(null);
    setError('');
    return true;
  }

  function selectRole(role: ProjectMemberRole) {
    setPendingRole(role);
    if (pendingEmployee) addMember(pendingEmployee, role);
  }

  function commitPendingMember() {
    if (!pendingEmployee) {
      setError('Select an employee.');
      return;
    }
    if (!pendingRole) {
      setError('Select a role for the chosen employee.');
      return;
    }
    addMember(pendingEmployee, pendingRole);
  }

  function removeMember(key: string) {
    setMembers(current => current.filter(member => member.key !== key));
  }

  function updateColumn(
    key: string,
    patch: Partial<ProjectColumnDraft>,
  ) {
    setColumns(current =>
      current.map(column => {
        if (column.key !== key) return column;
        const nextPatch = { ...patch };
        if (isFixedProjectColumn(column)) {
          delete nextPatch.columnName;
          delete nextPatch.isCompletedColumn;
          delete nextPatch.fixedSlot;
        }
        return { ...column, ...nextPatch };
      }),
    );
  }

  function removeColumn(key: string) {
    setColumns(current => {
      const target = current.find(column => column.key === key);
      if (!target) return current;
      if (isFixedProjectColumn(target)) {
        setError('The To-do and Done columns cannot be removed.');
        return current;
      }
      if (current.length <= 2) {
        setError('To-do and Done columns are required.');
        return current;
      }
      return pinFixedProjectColumns(current.filter(column => column.key !== key));
    });
  }

  function addColumn() {
    setColumns(current =>
      pinFixedProjectColumns([...current, createEmptyProjectColumn()]),
    );
  }

  function reorderMiddleColumns(middle: ProjectColumnDraft[]) {
    setColumns(current => {
      const start = current.filter(column => column.fixedSlot === 'start');
      const end = current.filter(column => column.fixedSlot === 'end');
      return [...start, ...middle, ...end];
    });
  }

  async function submit() {
    if (busy) return;
    const validationError = validateProjectForm({
      projectName,
      members,
      pendingEmployeeId: pendingEmployee?.id ?? null,
      pendingRole,
      columns,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setError('');
    try {
      await runOperation('add', () => createProject(
        toProjectCreatePayload({
          projectName,
          description,
          members,
          columns,
        }),
      ));
      navigation.goBack();
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to create the project.'));
    } finally {
      setBusy(false);
    }
  }

  const middleColumns = columns.filter(column => !isFixedProjectColumn(column));
  const startColumn = columns.find(column => column.fixedSlot === 'start');
  const endColumn = columns.find(column => column.fixedSlot === 'end');

  function renderColumnRow(
    item: ProjectColumnDraft,
    options?: { drag?: () => void; isActive?: boolean },
  ) {
    const fixed = isFixedProjectColumn(item);
    return (
      <View
        style={[
          styles.columnInlineRow,
          { borderBottomColor: palette.border },
          options?.isActive ? styles.columnInlineRowActive : null,
        ]}
      >
        {fixed || !options?.drag ? (
          <View style={styles.dragHandle}>
            <Text style={[styles.dragIcon, { color: palette.textMuted }, styles.dragIconDisabled]}>{fixed ? '' : '☰'}</Text>
          </View>
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Drag ${item.columnName.trim() || 'column'} to reorder`}
            onLongPress={options.drag}
            delayLongPress={100}
            style={styles.dragHandle}
          >
            <Text style={[styles.dragIcon, { color: palette.textMuted }]}>☰</Text>
          </Pressable>
        )}
        {fixed ? (
          <Text
            accessibilityLabel={`${item.columnName} column name`}
            style={[styles.columnInlineNameInput, themed.inputText, styles.columnFixedName]}
          >
            {item.columnName}
          </Text>
        ) : (
          <TextInput
            value={item.columnName}
            onChangeText={value => updateColumn(item.key, { columnName: value })}
            placeholder="Column name"
            placeholderTextColor={palette.textMuted}
            style={[styles.columnInlineNameInput, themed.inputText, { borderColor: palette.border }]}
          />
        )}
        <TextInput
          value={item.taskLimit}
          onChangeText={value => updateColumn(item.key, { taskLimit: value })}
          placeholder="None"
          placeholderTextColor={palette.textMuted}
          keyboardType="number-pad"
          accessibilityLabel={`Task limit for ${item.columnName.trim() || 'column'}`}
          style={[styles.columnInlineLimitInput, themed.inputText, { borderColor: palette.border }]}
        />
        {fixed ? (
          <View style={styles.deleteHandle} />
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Remove ${item.columnName.trim() || 'column'}`}
            onPress={() => removeColumn(item.key)}
            style={styles.deleteHandle}
          >
            <TrashIcon />
          </Pressable>
        )}
      </View>
    );
  }

  if (!capabilities.canCreateProject) return <UnauthorizedScreen />;

  return (
    <PermissionGuard
      permission={PERMISSIONS.PROJECT_CREATE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll keyboardSafe>
        <ScreenHeader title="Create project" onBack={navigation.goBack} />
        <View style={styles.body}>
          <AppCard style={styles.card}>
            <Text style={themed.section}>Project details</Text>
            <AppInput
              label="Project name"
              value={projectName}
              onChangeText={setProjectName}
              placeholder="Attendance Management System"
            />
            <AppInput
              label="Summary (optional)"
              value={description}
              onChangeText={setDescription}
              placeholder="Short project description"
            />
          </AppCard>

          <AppCard style={styles.card}>
            <Text style={themed.section}>Members</Text>
            <Text style={themed.hint}>
              Select an employee, then assign a role. The same employee cannot be added twice.
              {teamOnly
                ? ' Only employees in your team can be assigned, including you.'
                : ''}
            </Text>
            <SearchableEntitySelector
              label="Employee"
              selectedValue={pendingEmployee?.id ?? null}
              selectedLabel={pendingEmployee?.label ?? null}
              loadOptions={teamOnly ? loadTeamEmployeeOptions : loadOrganizationEmployeeOptions}
              onSelect={option => {
                setPendingEmployee(option);
                setPendingRole(null);
                setError('');
              }}
              onClear={() => {
                setPendingEmployee(null);
                setPendingRole(null);
              }}
              placeholder="Search employee"
            />
            {pendingEmployee ? (
              <View style={styles.roleBlock}>
                <Text style={themed.label}>Role</Text>
                <View style={styles.chipRow}>
                  {PROJECT_MEMBER_ROLES.map(role => {
                    const selected = pendingRole === role.value;
                    return (
                      <Pressable
                        key={role.value}
                        accessibilityRole="button"
                        accessibilityLabel={`Select ${role.label} role`}
                        onPress={() => selectRole(role.value)}
                        style={selected ? themed.chipActive : themed.chip}
                      >
                        <Text style={selected ? themed.chipTextActive : themed.chipText}>
                          {role.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            ) : null}
            <AppButton
              title="+ Add member"
              variant="secondary"
              onPress={commitPendingMember}
              disabled={busy}
            />

            {members.length > 0 ? (
              <View style={styles.listBlock}>
                <Text style={themed.label}>Assigned members</Text>
                {members.map(member => (
                  <View key={member.key} style={styles.memberRow}>
                    <Text style={themed.memberName}>{member.employeeName}</Text>
                    <Text style={themed.memberRole}>
                      {projectMemberRoleLabel(member.role)}
                    </Text>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Remove ${member.employeeName}`}
                      onPress={() => removeMember(member.key)}
                    >
                      <Text style={themed.remove}>Remove</Text>
                    </Pressable>
                  </View>
                ))}
              </View>
            ) : null}
          </AppCard>

          <AppCard style={styles.card}>
            <Text style={themed.section}>Columns</Text>
            <Text style={themed.hint}>
              To-do and Done stay first and last. Rename or reorder the columns between them.
              Leave task limit empty for no limit, or enter a number while creating the project.
            </Text>
            <View style={styles.columnHeaderRow}>
              <Text style={[themed.label, styles.columnNameHeader]}>Column name</Text>
              <Text style={[themed.label, styles.columnLimitHeader]}>Task limit</Text>
              <Text style={[themed.label, styles.columnRemoveHeader]}>Action</Text>
            </View>
            {startColumn ? renderColumnRow(startColumn) : null}
            <DraggableFlatList
              data={middleColumns}
              keyExtractor={item => item.key}
              onDragEnd={({ data }) => reorderMiddleColumns(data)}
              scrollEnabled={false}
              renderItem={({ item, drag, isActive }) =>
                renderColumnRow(item, { drag, isActive })
              }
            />
            {endColumn ? renderColumnRow(endColumn) : null}
            <AppButton
              title="+ Add column"
              variant="secondary"
              onPress={addColumn}
              disabled={busy}
            />
          </AppCard>

          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}
          <AppButton
            title={busy ? 'Saving…' : 'Save project'}
            onPress={submit}
            disabled={busy}
            loading={busy}
          />
          <AppButton
            title="Cancel"
            variant="secondary"
            onPress={() => navigation.goBack()}
            disabled={busy}
          />
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.md, paddingBottom: spacing.xl },
  card: { gap: spacing.md },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  roleBlock: { gap: spacing.sm },
  listBlock: { gap: spacing.sm },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  columnHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  columnNameHeader: { flex: 1 },
  columnLimitHeader: { width: 72, textAlign: 'center' },
  columnRemoveHeader: { width: 56, textAlign: 'right' },
  columnInlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  columnInlineRowActive: {
    opacity: 0.85,
  },
  dragHandle: {
    padding: spacing.xs,
  },
  dragIcon: {
    fontSize: 18,
  },
  dragIconDisabled: {
    opacity: 0,
  },
  deleteHandle: {
    padding: spacing.xs,
    minWidth: 26,
    minHeight: 26,
  },
  columnFixedName: {
    borderColor: 'transparent',
    fontWeight: '600',
  },
  columnInlineNameInput: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  columnInlineLimitInput: {
    width: 72,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    textAlign: 'center',
  },
});
